import { generateBFFUrl } from '@/configs/apiEndPoints';
import axiosInstance from '../axiosConfig';
import { AxiosResponse } from 'axios';
import { GenericError } from '@/errors/GenericServerError';
import { ERROR } from '@/configs/constants';
import {
  ECOM_PRODUCT_DETAIL,
  ECOM_SINGLE_PRODUCT_PRICES,
  ECOM_ADD_TO_CART,
} from '@/configs/ecom/apis';
import LocalStorage from '@/utils/localStorage';
import { EFL_CART_ID } from '@/constants/app';
import dlv from 'dlv';
import is from '@/utils/is';
import { MapType } from '@/types';
import seoResponseTransform from '@/utils/seoResponseTransform';
import generateHttpHeaders from '../axiosConfig/generateHttpHeaders';

const transformSpecifications = (specifications: any) => {
  const technicalInformationItems = dlv(specifications, 'technicalInformation.items');
  if (technicalInformationItems) {
    specifications.technicalInformation.list = specifications.technicalInformation.items.map(
      (item: any) => ({
        key: dlv(item, 'key.data.attributes.name'),
        value: item.value,
      }),
    );
    delete specifications.technicalInformation.items;
  }

  const productInformationItems = dlv(specifications, 'productInformation.items');
  if (productInformationItems) {
    specifications.productInformation.list = specifications.productInformation.items.map(
      (item: any) => ({
        key: dlv(item, 'key.data.attributes.name'),
        value: item.value,
      }),
    );
    delete specifications.productInformation.items;
  }
  return specifications;
};

export const getProductDetail = async (materialId: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(generateBFFUrl(ECOM_PRODUCT_DETAIL + materialId), {
      params: {
        populate:
          'seo,faqs.list,media,description,layout,included.list.icon,serviceBenefit.benefits.list.icon,specifications.productInformation.items.key,specifications.additionalFeatures.list,specifications.technicalInformation.items.key,specifications.downloadResources.list,variants,dealTag,signIn,sizingAndFitting.image,sizingAndFitting.video,sizingAndFitting.videoThumbnail,category,subCategories.category,purificationStages.mobileVideo,purificationStages.desktopVideo,aPlusContent.mobileImages,aPlusContent.desktopImages,quantity,flags,brand, expertGuidanceDemo.list.icon, expertGuidanceDemo.list.cta',
      },
    });
    const data = dlv(response, 'data.data', {});
    const specifications = dlv(data, 'attributes.specifications', null);
    if (specifications) {
      data.attributes.specifications = transformSpecifications(specifications);
    }
    seoResponseTransform(data.attributes.seo);
    return data;
  } catch (error: any) {
    console.error(error?.message);
    const { response } = error;
    const { data, status, statusText } = response as AxiosResponse;
    throw new GenericError(ERROR.GENERIC_MESSAGE, status, statusText, data);
  }
};

export const getProductPrices = async (materialIds: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(generateBFFUrl(ECOM_SINGLE_PRODUCT_PRICES), {
      params: {
        materialIds,
      },
    });
    const prices = dlv(response, 'data.data', []);
    const priceMaterialIdMap: MapType = {};
    if (is.not.empty.array(prices)) {
      prices.forEach((price: any) => {
        priceMaterialIdMap[price?.materialId] = price;
      });
    }
    return priceMaterialIdMap;
  } catch (error: any) {
    console.error(error?.message);
    const { response } = error;
    const { data, status, statusText } = response as AxiosResponse;
    throw new GenericError(ERROR.GENERIC_MESSAGE, status, statusText, data);
  }
};

export const getProducts = async (): Promise<any> => {
  try {
    return await axiosInstance.get(generateBFFUrl(ECOM_PRODUCT_DETAIL));
  } catch (error: any) {
    console.error(error?.message);
    const { response } = error;
    const { data, status, statusText } = response as AxiosResponse;
    throw new GenericError(ERROR.GENERIC_MESSAGE, status, statusText, data);
  }
};

export const getSingleProductPrices = async (
  materialIds: string[],
  cookie?: string,
): Promise<any> => {
  try {
    return await axiosInstance.get(generateBFFUrl(ECOM_SINGLE_PRODUCT_PRICES), {
      params: {
        materialIds,
      },
      headers: generateHttpHeaders(cookie),
    });
  } catch (error: any) {
    console.error(error?.message);
    const { response } = error;
    const { data, status, statusText } = response as AxiosResponse;
    throw new GenericError(ERROR.GENERIC_MESSAGE, status, statusText, data);
  }
};

export const addToCart = async (productDetails: string[]): Promise<any> => {
  let cartId = LocalStorage.get(EFL_CART_ID);
  try {
    return await axiosInstance.post(generateBFFUrl(ECOM_ADD_TO_CART), productDetails, {
      params: {
        cartId: cartId,
      },
    });
  } catch (error: any) {
    console.error(error?.message);
    const { response } = error;
    const { data, status, statusText } = response as AxiosResponse;
    if (status === 403) {
      LocalStorage.remove(EFL_CART_ID);
      return await axiosInstance.post(generateBFFUrl(ECOM_ADD_TO_CART), productDetails, {});
    }
    throw new GenericError(ERROR.GENERIC_MESSAGE, status, statusText, data);
  }
};
